/**
 * Acțiuni pentru workflow-ul „Mailjet Engagement → Lead", extrase din
 * NcrmWorkflowDetailsPage.vue ca metode (spread în `methods`) pentru a păstra
 * componenta-pagină sub politica de file-size (vezi docs/engineering/modularity.md).
 *
 * `this` este instanța NcrmWorkflowDetailsPage. Un singur dispatcher `run-node`
 * acoperă toate nodurile rulabile ale workflow-ului (trigger, existence-check, …).
 */
import {
  runMailjetTriggerForWorkflow,
  runContactExistenceForWorkflow,
  runLeadPresenceForWorkflow,
  runAiCompatibilityForWorkflow,
  runMailjetLeadAddForWorkflow,
  runMailjetTelegramNotifyForWorkflow,
  importMailjetCsv,
} from "@/Utile/ncrmStagingApi.mjs";

const MAILJET_NODE_RUNNERS = {
  "mailjet-event-trigger": {
    run: runMailjetTriggerForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.runResult", {
      created: Number(d?.created || 0),
      updated: Number(d?.updated || 0),
      skipped: Number(d?.skipped || 0),
    }),
  },
  "contact-existence-check": {
    run: runContactExistenceForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.existenceResult", {
      resolved: Number(d?.resolved || 0),
      notFound: Number(d?.not_found || 0),
    }),
  },
  "lead-presence-check": {
    run: runLeadPresenceForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.presenceResult", {
      advanced: Number(d?.advanced || 0),
      exists: Number(d?.already_in_board || 0),
    }),
  },
  "ai-compatibility-check": {
    run: runAiCompatibilityForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.aiResult", {
      compatible: Number(d?.compatible || 0),
      incompatible: Number(d?.incompatible || 0),
      queued: Number(d?.queued || 0),
      failed: Number(d?.failed || 0),
    }),
  },
  "mailjet-lead-add": {
    run: runMailjetLeadAddForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.leadResult", {
      added: Number(d?.added || 0),
      exists: Number(d?.already_exists || 0),
    }),
  },
  "telegram-notify": {
    run: runMailjetTelegramNotifyForWorkflow,
    message: (t, d) => t("ncrm.workflows.mailjetEngagement.telegramResult", {
      notified: Number(d?.notified || 0),
      status: String(d?.status || "skipped"),
    }),
  },
};

export const workflowDetailsMailjetActions = {
  async handleImportMailjetCsv(file) {
    if (!file || !this.selectedWorkflow?.id) return;
    this.loadingMailjetNode = true;
    this.errorMessage = "";
    try {
      const { data } = await importMailjetCsv(this.selectedWorkflow.id, file);
      this.infoMessage = this.$t("ncrm.workflows.mailjetEngagement.csvResult", {
        imported: Number(data?.imported || 0),
        skipped: Number(data?.skipped || 0),
        invalid: Number(data?.invalid || 0),
      });
      await this.fetchStagingRecords();
    } catch (error) {
      this.errorMessage =
        error?.response?.data?.error || this.$t("ncrm.workflows.mailjetEngagement.csvError");
    } finally {
      this.loadingMailjetNode = false;
    }
  },
  async handleRunMailjetNode(nodeKey) {
    const runner = MAILJET_NODE_RUNNERS[nodeKey];
    if (!runner || !this.selectedWorkflow?.id) return;
    this.loadingMailjetNode = true;
    this.errorMessage = "";
    try {
      const { data } = await runner.run(this.selectedWorkflow.id);
      this.infoMessage = runner.message(this.$t, data);
      await this.fetchStagingRecords();
      await this.loadWorkflows();
    } catch (error) {
      this.errorMessage =
        error?.response?.data?.error || this.$t("ncrm.workflows.mailjetEngagement.runError");
    } finally {
      this.loadingMailjetNode = false;
    }
  },
};
